import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Box, Typography, Breadcrumbs, Divider, Card, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { getAppReleaseByIdStore, setAppRelease } from 'store/app-releases';
import { useResponseHandler } from 'hooks/useResponseHandler';
import { StatusChip } from 'commons/generics/StatusChip';
import { formatDateToYmd } from 'commons/utils';

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: 50,
  padding: 24,
  borderRadius: 16,
  boxShadow: '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
  border: '1px solid rgba(145, 158, 171, 0.16)'
}));

export const DetailNovedadesRelease = () => {
  const { id } = useParams();
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const { app_release } = useSelector((state: any) => state.appReleases);
  const { handleResponse, ResponseComponents } = useResponseHandler() as any;

  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        const response = await dispatch(getAppReleaseByIdStore(id));
        if (!response?.ok) {
            navigate('/manager/novedades');
        }
      }
    };
    fetchData();

    return () => {
      dispatch(setAppRelease(null));
    };
  }, [dispatch, id, navigate]);

  return (
    <Box sx={{ width: '100%' }}>
      {ResponseComponents}

      <Typography fontSize={25} color="textPrimary" sx={{ fontWeight: 700 }}>Detalle de Novedad (Release)</Typography>
      <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
          <Typography color="textPrimary" sx={{ fontWeight: 700 }}>Manager</Typography>
          <Link  to="/manager/novedades">
            Listar Novedades
          </Link>
          <Typography color="textPrimary">Detalle Novedad</Typography>
      </Breadcrumbs>
      <Divider sx={{ marginBottom: 3 }}/>

      <StyledCard>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
            Información General
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Versión</Typography>
              <Typography variant="body1" fontWeight="500">{app_release?.version || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Aplicación</Typography>
              <Typography variant="body1" fontWeight="500">{app_release?.application?.name || 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Fecha de Lanzamiento</Typography>
              <Typography variant="body1" fontWeight="500">{app_release?.releasedAt ? formatDateToYmd(app_release.releasedAt) : 'N/A'}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Typography variant="subtitle2" color="textSecondary">Estado</Typography>
              <Box sx={{ mt: 0.5 }}>
                 <StatusChip status={app_release?.status ?? 0} />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" color="primary" sx={{ mb: 3 }}>
            Novedades / Features
          </Typography>
          
          {app_release?.features?.length > 0 ? (
            app_release.features.map((feature: any, index: number) => (
              <Box key={index} sx={{ mb: 3, p: 3, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
                <Typography variant="subtitle1" color="primary" fontWeight="bold" sx={{ mb: 1 }}>{feature.title}</Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>Módulo: {feature.moduleName}</Typography>
                <Typography variant="body1">{feature.description}</Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body1" color="textSecondary">No hay novedades registradas.</Typography>
          )}
        </Box>
      </StyledCard>
    </Box>
  );
};